import { css } from 'lit';

/** Breakpoint móvil estándar del proyecto (max-width). */
export const MOBILE_BREAKPOINT = '639px';

/** Reset de box-sizing para Shadow DOM (no hereda el global de document). */
export const boxSizingStyles = css`
  :host {
    box-sizing: border-box;
    max-width: 100%;
  }

  *,
  *::before,
  *::after {
    box-sizing: border-box;
  }
`;

/** Utilidades responsive reutilizables en páginas Lit. */
export const responsiveStyles = css`
  :host {
    display: block;
    max-width: 100%;
  }

  .stack-mobile {
    display: flex;
  }

  @media (max-width: 639px) {
    .stack-mobile {
      flex-direction: column;
      align-items: stretch;
    }

    .hero {
      padding: var(--space-lg);
    }
  }
`;

/** Estilos compartidos para uso dentro de Shadow DOM (Lit). */
export const cardStyles = css`
  .card {
    position: relative;
    padding: var(--space-xl);
    border-radius: var(--radius-lg);
    background: var(--color-surface);
    backdrop-filter: blur(24px);
    -webkit-backdrop-filter: blur(24px);
    box-shadow: var(--shadow-md);
  }

  .card::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: var(--gradient-border);
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    opacity: 0.65;
  }

  .card--glow {
    box-shadow: var(--shadow-glow-primary), var(--shadow-md);
  }
`;

export const alertStyles = css`
  .alert {
    display: flex;
    align-items: flex-start;
    gap: var(--space-sm);
    padding: var(--space-sm) var(--space-md);
    border-radius: var(--radius-sm);
    font-size: 0.875rem;
    line-height: 1.5;
    justify-content: center;
  }

  .alert--success {
    background: rgb(16 185 129 / 10%);
    color: var(--color-success);
    border: 1px solid rgb(52 211 153 / 25%);
  }

  .alert--error {
    background: rgb(244 63 94 / 10%);
    color: var(--color-error);
    border: 1px solid rgb(251 113 133 / 25%);
  }
`;

export const motionStyles = css`
  @keyframes pulse-glow {
    0%, 100% { opacity: 0.5; }
    50% { opacity: 0.85; }
  }

  @keyframes fade-up {
    from {
      opacity: 0;
      transform: translateY(12px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .animate-fade-up {
    animation: fade-up var(--duration-slow) var(--ease-out) both;
  }

  .spinner {
    width: 2rem;
    height: 2rem;
    border: 2px solid var(--color-border-strong);
    border-top-color: var(--palette-violet-500);
    border-radius: 50%;
    animation: spin 0.7s linear infinite;
  }
`;

export const statStyles = css`
  .stat-grid {
    display: grid;
    gap: var(--space-md);
    grid-template-columns: 1fr;
  }

  @media (min-width: 640px) {
    .stat-grid {
      grid-template-columns: repeat(2, 1fr);
    }
  }

  .stat-card {
    position: relative;
    padding: var(--space-lg);
    border-radius: var(--radius);
    background: var(--color-surface);
    border: 1px solid var(--color-border);
    backdrop-filter: blur(12px);
    -webkit-backdrop-filter: blur(12px);
    overflow: hidden;
    transition:
      border-color var(--duration-normal) var(--ease-out),
      transform var(--duration-normal) var(--ease-out),
      box-shadow var(--duration-normal) var(--ease-out);
  }

  .stat-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 2px;
    background: var(--gradient-primary);
    opacity: 0.7;
  }

  .stat-card:hover {
    border-color: var(--color-border-glow);
    transform: translateY(-2px);
    box-shadow: var(--shadow-glow-primary);
  }

  .stat-card--highlight::before {
    background: var(--gradient-credits);
    opacity: 1;
    height: 3px;
  }

  .stat-label {
    display: flex;
    align-items: center;
    gap: var(--space-xs);
    font-size: 0.8125rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--color-text-muted);
    margin-bottom: var(--space-sm);
  }

  .stat-value {
    font-size: 1.625rem;
    font-weight: 700;
    letter-spacing: -0.02em;
    color: var(--color-text);
  }

  .stat-value--gradient {
    background: var(--gradient-credits);
    -webkit-background-clip: text;
    background-clip: text;
    -webkit-text-fill-color: transparent;
  }

  .stat-value--email {
    font-size: 1rem;
    font-weight: 600;
    word-break: break-all;
  }

  .stat-icon {
    font-size: 0.875rem;
    opacity: 0.8;
  }
`;

/** Estilos de prosa para artículos y páginas legales. */
export const proseStyles = css`
  .article-body {
    font-size: 1.0625rem;
    line-height: 1.75;
    color: var(--color-text);
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .article-body :is(h1, h2, h3) {
    margin-top: var(--space-xl);
    margin-bottom: var(--space-sm);
  }

  .article-body h1 {
    font-size: 1.5rem;
    font-weight: 700;
    letter-spacing: -0.02em;
  }

  .article-body h2 {
    font-size: 1.375rem;
    color: var(--color-primary-light);
  }

  .article-body h3 {
    font-size: 1.125rem;
  }

  .article-body p {
    margin: 0 0 var(--space-md);
  }

  .article-body ul,
  .article-body ol {
    margin: 0 0 var(--space-md);
    padding-left: 1.25rem;
  }

  .article-body li {
    margin-bottom: 0.375rem;
  }

  .article-body a {
    color: var(--color-primary-light);
    text-decoration: underline;
    text-underline-offset: 2px;
  }

  .article-body .md-table-wrap {
    overflow-x: auto;
    margin: var(--space-lg) 0;
    border-radius: var(--radius-sm);
    border: 1px solid var(--color-border);
  }

  .article-body table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9375rem;
  }

  .article-body th,
  .article-body td {
    padding: var(--space-sm);
    border: 1px solid var(--color-border);
    text-align: left;
    vertical-align: top;
  }

  .article-body th {
    background: var(--color-surface-hover);
  }
`;
